import { getAllUserNames, getUsers } from "./account";

/**
 * One place to turn a user id into something a human can read.
 *
 * `getDisplayName` was duplicated byte-for-byte in PaidUsers and Subscriptions,
 * and Billing kept a third, narrower variant of its own that only covered
 * workspace members. Credits are usually granted by an admin from this panel,
 * who is not a member of the workspace they are granting to, so the narrow map
 * always fell through to the raw ObjectId.
 */

export type UserNameMap = Record<string, string>;

/**
 * Resolves either a raw id or an embedded user object to a display name.
 * Falls back through the shapes the various APIs return, and finally to the id
 * itself so a row never renders blank.
 */
export const getDisplayName = (value: any, userNames: UserNameMap = {}) => {
  if (!value) return "-";
  if (typeof value === "string") return userNames[value] || value;

  const fullName = [value.firstName, value.lastName].filter(Boolean).join(" ");
  const userId = value._id || value.id;
  return (
    value.name ||
    fullName ||
    value.email ||
    value.userName ||
    value.displayName ||
    (userId ? userNames[userId] : "") ||
    userId ||
    "-"
  );
};

/** Builds an id → display name map from a list of user objects. */
export const buildUserNameMap = (users: any[] = []): UserNameMap =>
  users.reduce<UserNameMap>((map, user) => {
    const id = user?.id || user?._id;
    if (id) {
      map[id] = getDisplayName(user);
    }
    return map;
  }, {});

/**
 * Every user in the org, so ids that belong to admins rather than workspace
 * members still resolve. Resolves to an empty map on failure — a missing name is
 * a cosmetic problem and must not take a page down with it.
 */
export const fetchAllUserNames = async (): Promise<UserNameMap> => {
  try {
    // `/api/v1/get-all-users` rather than the heavier `users-manage` list — this
    // is the endpoint PaidUsers and Subscriptions already use for name lookups.
    const response: any = await getAllUserNames();
    return buildUserNameMap(response?.users || []);
  } catch (error) {
    console.error("Error fetching user names:", error);
    return {};
  }
};

/**
 * Members of one workspace. `users-manage` nests the user under each membership
 * record, unlike the org-wide endpoint.
 */
export const fetchWorkspaceUserNames = async (
  workspaceId: string,
): Promise<UserNameMap> => {
  try {
    const response: any = await getUsers(workspaceId);
    const members = (response?.data || [])
      .map((membership: any) => membership?.user)
      .filter(Boolean);
    return buildUserNameMap(members);
  } catch (error) {
    console.error("Error fetching workspace user names:", error);
    return {};
  }
};
